/**
 * safari-video-autoplay.js
 * Forces <video autoplay muted playsinline> to actually start in Safari, including
 * iOS / macOS Low Power Mode (battery saver) where the spec-compliant attributes
 * are silently ignored and the video stays on its first frame with a tappable
 * play-overlay.
 *
 * Origin:
 *   Adapted from the busy.bar Tilda hero-video script (Andrei Kozlov, ~2024).
 *   Original was Tilda-specific (`.b-desktop-video` / `.b-mobile-video` /
 *   `.bg-video-cover` selectors + a detach/re-attach into `.tn-atom__videoiframe`).
 *   This version is a generic shim for the Webflow-track exports we use here.
 *
 * Loaded from:
 *   layout/theme.liquid, with `defer`, after webflow.js / busyapp-webflow.js.
 *   Safe to load globally — no-op when the page has no autoplay videos.
 *
 * Why a "minimal" reorder isn't enough for Safari LPM:
 *   In Low Power Mode Safari treats autoplay as a *deliberate* user-cost request.
 *   Just toggling attributes after page load isn't enough — Safari has already
 *   classified the element. The reliable workaround (the one Tilda used and we
 *   verified again in 2026-04) is to make Safari re-evaluate the element from
 *   scratch:
 *     1) Set preload="none" so source isn't fetched yet.
 *     2) Reorder attributes: muted -> playsinline -> autoplay (muted MUST be
 *        evaluated before autoplay).
 *     3) Detach + re-attach the <video> to its parent. Safari treats the
 *        re-attached node as a fresh element and re-runs its autoplay
 *        admission with our new attribute order in scope.
 *     4) Call video.load() to start fetching now that it's "new".
 *     5) On canplaythrough/loadeddata: explicitly call .play(). Even if Safari
 *        admits autoplay, sometimes the first .play() is needed to actually start.
 *   Plus three fallback layers:
 *     - IntersectionObserver: re-try .play() every time the video enters viewport.
 *     - First user gesture (touchstart/click) and tab visibilitychange.
 *     - rAF burst for 2s after rescue: brute-force retry while Safari decides.
 *
 * Idempotent: each <video> is rescued at most once (data-safari-autoplay-rescued).
 *
 * Why this is safe to do without a cover overlay (Tilda used one):
 *   The detach/re-attach happens BEFORE any frame is painted (the rescue runs
 *   on the still-paused first-frame state). No visible flicker results, because
 *   the only frame Safari has at that point is the same first frame anyway.
 */
(function () {
  'use strict';

  var DATA_FLAG = 'safariAutoplayRescued';
  var BURST_DURATION_MS = 2000;
  var rescued = [];

  function softPlay(video) {
    if (!video.paused) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { /* swallow — fallback layers will retry */ });
    }
  }

  function rescueVideo(video) {
    if (!video || video.dataset[DATA_FLAG] === '1') return;
    video.dataset[DATA_FLAG] = '1';

    // (1) Disable preload so the rescue starts from a clean state.
    var originalPreload = video.getAttribute('preload');
    video.setAttribute('preload', 'none');

    // (2) Reorder attributes — muted MUST be present before autoplay for
    // Safari to admit the autoplay.
    video.removeAttribute('autoplay');
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    // (3) Detach + re-attach: forces Safari to re-evaluate autoplay policy
    // for the element with our new attribute order in scope. This is THE
    // key step for Low Power Mode — the previous "minimal" version skipped
    // it and LPM kept blocking autoplay.
    var parent = video.parentNode;
    if (parent) {
      var nextSibling = video.nextSibling;
      parent.removeChild(video);
      if (nextSibling) {
        parent.insertBefore(video, nextSibling);
      } else {
        parent.appendChild(video);
      }
    }

    // (4) Re-enable preload (back to whatever the markup had, default 'auto'
    // for background videos) and force load.
    if (originalPreload) {
      video.setAttribute('preload', originalPreload);
    } else {
      video.removeAttribute('preload');
    }
    try { video.load(); } catch (_) { /* iOS sometimes throws on load() during init */ }

    // (5) Force play once data is buffered enough. Once the element is actually
    // playing these three have nothing left to do, so drop them instead of
    // keeping three listeners per video alive for the life of the page.
    var play = function () { softPlay(video); };
    video.addEventListener('canplaythrough', play);
    video.addEventListener('loadeddata', play);
    video.addEventListener('canplay', play);
    video.addEventListener('playing', function () {
      video.removeEventListener('canplaythrough', play);
      video.removeEventListener('loadeddata', play);
      video.removeEventListener('canplay', play);
    }, { once: true });

    // Fallback A: rAF burst — keep trying for the first ~2 seconds after rescue.
    // Safari sometimes admits autoplay only after the source is partially
    // buffered, which can land mid-burst.
    var burstStart = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
    var burst = function () {
      if (!video.paused) return;
      var now = (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
      if (now - burstStart > BURST_DURATION_MS) return;
      softPlay(video);
      requestAnimationFrame(burst);
    };
    requestAnimationFrame(burst);

    // Fallback B: re-try whenever the video enters the viewport. Safari often
    // pauses off-screen background videos in LPM and won't auto-resume.
    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) softPlay(video);
        }
      }, { threshold: 0.01 });
      io.observe(video);
    }

    // Fallback C: first user gesture. Tab visibility is handled once for all
    // rescued videos below, not per element.
    var gestureOpts = { once: true, passive: true, capture: true };
    var clickOpts = { once: true, capture: true };
    window.addEventListener('touchstart', play, gestureOpts);
    window.addEventListener('click', play, clickOpts);
    rescued.push(video);

    // Initial kick (covers the rare case where data is already buffered).
    softPlay(video);
  }

  // Coming back to the tab used to fire one handler per rescued video, each of
  // which called play() unconditionally — including on clips far outside the
  // viewport that the page had deliberately paused. One handler, and only for
  // what is on screen.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    for (var i = 0; i < rescued.length; i++) {
      var box = rescued[i].getBoundingClientRect();
      var onScreen = box.width > 0 && box.height > 0 &&
        box.bottom > 0 && box.top < (window.innerHeight || document.documentElement.clientHeight);
      if (onScreen) softPlay(rescued[i]);
    }
  });

  // Videos marked [data-lazy-video] have no source until they scroll into view
  // (see sections/shop-webflow.liquid). Rescuing them here would be pointless —
  // there is nothing to load yet — so the owner calls busyRescueVideo() itself
  // right after attaching the source, and still gets the Safari LPM handling.
  window.busyRescueVideo = rescueVideo;

  function rescueAll() {
    var videos = document.querySelectorAll(
      'video[autoplay]:not([data-lazy-video]), .w-background-video video:not([data-lazy-video])'
    );
    for (var i = 0; i < videos.length; i++) {
      rescueVideo(videos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rescueAll);
  } else {
    rescueAll();
  }
})();
